import Hls, { ErrorDetails, ErrorTypes, Events } from "hls.js";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  ApiError,
  api,
  asQuotaExhausted,
  notifyUnauthorized,
  stopStreamOnUnload,
  type QuotaExhausted,
} from "../../lib/api";
import { createRecoveryBudget } from "./recoveryBudget";
import { type PlayerAction, holdsLease, initialPlayerState, playerReducer } from "./playerState";
import type { StreamSlots } from "./streamSlots";

// Renewal cadence: comfortably inside the backend's lease TTL (2 minutes by
// default) so a slow tick or a brief network blip never lets the lease lapse
// before the next attempt.
const RENEW_INTERVAL_MS = 40_000;
const RENEW_RETRY_DELAYS_MS = [3_000, 6_000, 12_000];

// Bounds how long a tile waits for first playback before giving up.
//
// A camera that is unreachable (wrong address, powered off, network path
// down) never produces an HLS manifest — the backend just answers "starting"
// forever while it retries the RTSP dial with its own backoff. hls.js's
// default manifest-load retry policy is generous enough that a tile in that
// state sits on a spinner indefinitely with no error ever surfacing, which is
// the wrong failure mode for an operations dashboard: "this camera is down"
// needs to be obvious, not silent. This timer is the backstop that turns
// silence into a visible, retryable error state.
const CONNECT_TIMEOUT_MS = 25_000;

// How long a tile refused a slot waits before trying again once one frees.
// Randomised across the range because a release wakes every waiting tile at
// the same instant: without the spread they would all call start() together,
// one would win, and the rest would immediately be refused again.
const QUEUE_RETRY_MIN_MS = 150;
const QUEUE_RETRY_JITTER_MS = 350;

// How long a tile waits before retrying once hls.js's own fast recovery
// (recoveryBudget.ts) is spent. Matches the backend's own RTSP reconnect
// ceiling (see streaming.Config.ReconnectMax) rather than polling faster than
// the thing on the other end can possibly respond — the backend retries
// forever at this same cadence once it gets there, so this browser trying
// more often than that only wastes a request every time.
const RECONNECT_RETRY_DELAY_MS = 30_000;

// Bounds how long a tile can sit in "buffering" with no progress before this
// browser forces a retry itself, rather than waiting indefinitely for hls.js
// to notice on its own.
//
// hls.js only raises a fatal Events.ERROR for requests that actually fail —
// if the backend's response is technically succeeding (a live playlist that
// simply is not advancing, which is exactly what a wedged upstream pull looks
// like from here) hls.js has nothing to call an error, and the tile is stuck
// silently. Longer than a normal keyframe-interval hiccup (see
// SegmentMinDuration's sibling comment in the backend) so a brief re-buffer
// never trips it, short enough that an operator is not staring at a frozen
// tile for a minute.
const STALL_WATCHDOG_MS = 15_000;

// How often the progress watchdog samples the playhead once the stream is
// confirmed live (see startProgressWatchdog's own comment for why this needs
// to exist independent of STALL_WATCHDOG_MS above). Deliberately NOT tied to
// FRAG_LOADED's own cadence (roughly one per segment — SegmentMinDuration in
// the backend): a check reset every time a fragment loads would never get
// far enough to notice a playhead that is wedged while downloads keep
// succeeding, which is exactly the failure mode this watchdog exists to
// catch.
const PROGRESS_CHECK_INTERVAL_MS = 5_000;

interface UseHlsStreamOptions {
  cameraId: string;
  /** Unique per rendered tile — usually the camera id, but distinct if a
   *  camera can appear twice (e.g. a pinned tile plus the grid). */
  tileId: string;
  slots: StreamSlots;
  enabled: boolean;
  /** Start without being asked, because this camera is ALREADY being pulled
   *  for somebody else — joining a running stream costs nothing, since the
   *  server keeps one RTSP session per camera and shares it. False for a
   *  camera nobody is watching: the first viewer asks for it deliberately
   *  rather than the wall committing the site's uplink on their behalf.
   *
   *  Defaults false so a caller that has not thought about it does not
   *  silently start streaming. */
  autoStart?: boolean;
}

export function useHlsStream({
  cameraId,
  tileId,
  slots,
  enabled,
  autoStart = false,
}: UseHlsStreamOptions) {
  const [state, dispatch] = useReducer(playerReducer, initialPlayerState);
  // Read by effects that must not re-run when the state changes — see the
  // auto-start effect for why depending on `state` there would be wrong.
  const stateRef = useRef(state);
  stateRef.current = state;

  // The detail of a refused start, held so the caller can ask the operator
  // whether to make room. Cleared as soon as a start succeeds or is dismissed.
  const [contention, setContention] = useState<QuotaExhausted | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const leaseIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const renewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Baseline and consecutive-no-progress count for the progress watchdog.
  // Plain refs, not state: sampled and updated once a tick, never rendered.
  const lastProgressTimeRef = useRef(0);
  const stalledChecksRef = useRef(0);
  const recoveryRef = useRef(createRecoveryBudget());

  // The generation counter is what makes teardown correct under rapid
  // start/stop: every async step below checks it before touching state, so a
  // resolver that lands after the run it belongs to has already ended is a
  // no-op instead of resurrecting a torn-down tile.
  const generationRef = useRef(0);

  const dispatchSafe = useCallback((action: PlayerAction, generation: number) => {
    if (generationRef.current === generation) dispatch(action);
  }, []);

  const clearRenewTimer = useCallback(() => {
    if (renewTimerRef.current !== null) {
      clearTimeout(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  }, []);

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current !== null) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearStallWatchdog = useCallback(() => {
    if (stallWatchdogRef.current !== null) {
      clearTimeout(stallWatchdogRef.current);
      stallWatchdogRef.current = null;
    }
  }, []);

  const clearProgressWatchdog = useCallback(() => {
    if (progressWatchdogRef.current !== null) {
      clearInterval(progressWatchdogRef.current);
      progressWatchdogRef.current = null;
    }
  }, []);

  const teardownPlayer = useCallback(() => {
    clearRenewTimer();
    clearConnectTimeout();
    clearReconnectTimer();
    clearStallWatchdog();
    clearProgressWatchdog();
    // Aborts the video-level "playing"/"waiting" listeners attachPlayer adds
    // (see the `{ signal }` option there). Without this, a tile that's
    // suspended and resumed repeatedly (focus mode toggling a grid tile's
    // `enabled`, or a manual Retry) would accumulate a fresh pair of
    // listeners on the same <video> element every run — harmless per-dispatch
    // (dispatchSafe already no-ops a stale generation) but an unbounded
    // memory/listener leak over a long shift's worth of toggles.
    abortRef.current?.abort();
    if (hlsRef.current) {
      hlsRef.current.detachMedia();
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, [clearRenewTimer, clearConnectTimeout, clearReconnectTimer, clearStallWatchdog, clearProgressWatchdog]);

  // Releases the slot FIRST and unconditionally, before checking whether a
  // lease was ever acquired. A start that fails before getting a lease still
  // claimed a slot; leaking it on every such failure would shrink the whole
  // grid's budget over time for no visible reason.
  const releaseLease = useCallback(() => {
    slots.release(tileId);
    const leaseId = leaseIdRef.current;
    if (!leaseId) return;
    leaseIdRef.current = null;
    api.stream.stop(cameraId, leaseId).catch(() => {
      // Best-effort: the grace window on the server reclaims it regardless.
    });
  }, [cameraId, tileId, slots]);

  // A tile whose fast automatic recovery is spent (recoveryBudget.ts) retries
  // here instead of dead-ending — see PlayerState's own comment on
  // "reconnecting" for the product decision behind this. Reads start() via
  // startRef rather than closing over it directly: this timer must survive
  // across renders and call whatever start() currently is, the same reason
  // the queued-retry effect further below does the same thing. If the retry
  // itself lands back on `recovery_exhausted`, RECOVERY_EXHAUSTED fires again
  // and this simply reschedules — the loop is the point.
  //
  // Declared before scheduleRenew (which now calls this on a renew ladder
  // exhausted by anything short of a confirmed-gone lease) rather than after
  // it: a useCallback dependency array is evaluated immediately, at render
  // time, unlike the callback body itself — putting scheduleReconnect in
  // scheduleRenew's deps while it was still declared further down threw
  // "Cannot access 'scheduleReconnect' before initialization" on every
  // render, caught by simply loading the page once after wiring this up.
  const scheduleReconnect = useCallback(
    (generation: number) => {
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        if (generationRef.current !== generation) return;
        startRef.current();
      }, RECONNECT_RETRY_DELAY_MS);
    },
    [clearReconnectTimer],
  );

  // Independent evidence that the picture is actually moving, not just that
  // the backend is delivering data. FRAG_LOADED (and the "playing" listener
  // in attachPlayer) prove the latter, but a wedged playhead — a gap the
  // gap-controller never resolves, a decoder stuck on a corrupt frame — can
  // coexist with segments arriving on schedule: hls.js has nothing to call
  // an error over, and every FRAG_LOADED keeps clearing STALL_WATCHDOG and
  // re-dispatching PLAYING regardless, so the tile shows a frozen frame
  // under a pulsing "Live" dot with no panel, no spinner, and nothing to
  // click.
  //
  // Idempotent rather than restarted on every PLAYING confirmation — a later
  // FRAG_LOADED or "playing" event must not reset this watchdog's own
  // baseline, or a stream that keeps loading fragments while the playhead is
  // stuck would keep the count from ever accumulating, the same way it
  // already defeats STALL_WATCHDOG. Once armed for a generation it samples
  // on its own schedule until teardownPlayer clears it.
  const startProgressWatchdog = useCallback(
    (generation: number) => {
      if (progressWatchdogRef.current !== null) return; // already running for this generation
      stalledChecksRef.current = 0;
      lastProgressTimeRef.current = videoRef.current?.currentTime ?? 0;
      progressWatchdogRef.current = setInterval(() => {
        if (generationRef.current !== generation) return;
        const video = videoRef.current;
        // A deliberately paused frame (the operator holding it, see pause()'s
        // own comment) or an in-flight seek is not a stall — currentTime not
        // advancing is exactly the point. Reset rather than accumulate, so
        // resuming starts a fresh window instead of immediately tripping on
        // whatever count built up while paused.
        if (!video || video.paused || video.seeking) {
          stalledChecksRef.current = 0;
          lastProgressTimeRef.current = video?.currentTime ?? lastProgressTimeRef.current;
          return;
        }
        if (video.currentTime > lastProgressTimeRef.current) {
          lastProgressTimeRef.current = video.currentTime;
          stalledChecksRef.current = 0;
          return;
        }
        stalledChecksRef.current += 1;
        if (stalledChecksRef.current * PROGRESS_CHECK_INTERVAL_MS < STALL_WATCHDOG_MS) return;
        // The same outcome the "waiting"-driven stall watchdog produces, and
        // the same threshold (STALL_WATCHDOG_MS) — this is the same
        // judgment call ("how long stuck is too long"), just measured a
        // different way because the browser never told us this one was
        // waiting.
        teardownPlayer();
        releaseLease();
        dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
        scheduleReconnect(generation);
      }, PROGRESS_CHECK_INTERVAL_MS);
    },
    [teardownPlayer, releaseLease, dispatchSafe, scheduleReconnect],
  );

  const scheduleRenew = useCallback(
    (generation: number, attempt = 0) => {
      clearRenewTimer();
      renewTimerRef.current = setTimeout(
        async () => {
          if (generationRef.current !== generation) return;
          const leaseId = leaseIdRef.current;
          if (!leaseId) return;

          try {
            await api.stream.renew(cameraId, leaseId);
            if (generationRef.current !== generation) return;
            scheduleRenew(generation, 0);
          } catch (err: unknown) {
            if (generationRef.current !== generation) return;

            // The backend answers 410 Gone specifically and only when the
            // lease is confirmed gone (domain.ErrLeaseNotFound) — not for a
            // network blip, a timeout, or any other renew failure, all of
            // which fall through to the retry ladder below. Retrying /renew
            // against a 410 can never succeed: the server has already
            // forgotten this lease. Skip straight to acquiring a fresh one
            // instead of burning the 3s/6s/12s ladder on a request that is
            // guaranteed to fail every time.
            if (err instanceof ApiError && err.status === 410) {
              teardownPlayer();
              releaseLease();
              startRef.current();
              return;
            }

            if (attempt < RENEW_RETRY_DELAYS_MS.length) {
              const delay = RENEW_RETRY_DELAYS_MS[attempt] ?? 12_000;
              renewTimerRef.current = setTimeout(() => scheduleRenew(generation, attempt + 1), delay);
            } else {
              // Ladder exhausted on a failure that was never confirmed as
              // "the lease is gone" (a sustained network blip, say) — join
              // the same retry-forever loop as every other exhausted-
              // recovery path (recoveryBudget.ts, the stall watchdog, the
              // initial connect timeout) rather than dead-ending in a
              // manual-retry error. A lost lease is recoverable by
              // definition: a fresh start() mints a new one, the same as it
              // would for a brand new tile.
              teardownPlayer();
              releaseLease();
              dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
              scheduleReconnect(generation);
            }
          }
        },
        attempt === 0 ? RENEW_INTERVAL_MS : 0,
      );
    },
    [cameraId, clearRenewTimer, dispatchSafe, releaseLease, teardownPlayer, scheduleReconnect],
  );

  const start = useCallback((evictCameraID?: string) => {
    // Acquire the slot BEFORE any network call: the camera must never be
    // commanded to start a stream that will just be refused for lack of room.
    //
    // Being refused is not a failure — the tile goes to "queued" and the
    // effect below starts it as soon as another tile stops.
    if (!slots.acquire(tileId)) {
      dispatch({ type: "QUEUED" });
      return;
    }

    if (!Hls.isSupported() && !videoRef.current?.canPlayType("application/vnd.apple.mpegurl")) {
      slots.release(tileId);
      dispatch({ type: "FAILED", code: "unsupported_browser" });
      return;
    }

    const generation = ++generationRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // A fresh run gets a fresh budget. Without this, a tile suspended and
    // resumed several times (focus mode toggling `enabled`, or Retry) starts
    // each new attempt with whatever budget the *previous* attempt had left,
    // so a camera with one earlier hiccup looks closer to "exhausted" than it
    // actually is.
    recoveryRef.current = createRecoveryBudget();

    setContention(null);
    dispatch({ type: "PLAY_REQUESTED" });
    dispatchSafe({ type: "PHASE_CHANGED", phase: "connecting" }, generation);

    // A camera that never comes up (wrong address, powered off, or — the
    // case this loop exists for — a flaky upstream link still mid-recovery)
    // otherwise leaves this tile spinning forever — see the constant's own
    // comment. Lands in the same retry-forever loop as a lost-after-playing
    // stream (RECOVERY_EXHAUSTED + scheduleReconnect), not a dead-end
    // `error`: a camera that simply hasn't come up YET is exactly the
    // "still trying" case the confirmed product decision covers, and a
    // fresh tile racing a currently-degraded NVR has no better reason to
    // demand a manual click than one that was already playing and stalled.
    // VideoTile's own fast path (retry immediately once server health says
    // "ready") applies here too for free, since it keys off `reconnecting`
    // generically rather than how the tile got there.
    clearConnectTimeout();
    connectTimeoutRef.current = setTimeout(() => {
      if (generationRef.current !== generation) return;
      teardownPlayer();
      releaseLease();
      dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
      scheduleReconnect(generation);
    }, CONNECT_TIMEOUT_MS);

    // Starting by stopping somebody else's stream is a different request with
    // a different permission, so it is a different endpoint — not a flag on
    // this one. See the router's own comment.
    const request = evictCameraID ? api.stream.startEvicting(cameraId, evictCameraID) : api.stream.start(cameraId);

    request
      .then((res) => {
        if (generationRef.current !== generation) {
          // This run was torn down while the request was in flight — a fast
          // filmstrip switch in focus mode is the routine way to hit this,
          // not just a rare race. The server grants the lease before it even
          // starts the RTSP pull (so it's already real by the time this
          // response lands), and nothing else refers to it: dropping it
          // silently here would leak an RTSP pull for the full lease
          // TTL + grace window for every such switch.
          api.stream.stop(cameraId, res.lease_id).catch(() => {
            // Best-effort: the server's own grace window reclaims it regardless.
          });
          return;
        }
        leaseIdRef.current = res.lease_id;
        scheduleRenew(generation);
        dispatchSafe({ type: "PHASE_CHANGED", phase: "attaching_player" }, generation);
        // Built from our own known API base, not res.playlist_url: the
        // backend's own view of its public URL can't be trusted in every
        // deployment shape (behind a proxy, a different origin in dev), and
        // this client already knows exactly where its API lives.
        attachPlayer(api.hlsPlaylistUrl(cameraId), generation, controller.signal);
      })
      .catch((err: unknown) => {
        if (generationRef.current !== generation) return;
        slots.release(tileId);
        // A quota refusal is not a fault and not retryable on its own: the
        // wall is full until a limit is raised or something stops. It carries
        // what would have to stop, so the caller can offer that as a choice
        // rather than a dead end.
        const quota = asQuotaExhausted(err);
        if (quota) {
          setContention(quota);
          dispatchSafe({ type: "FAILED", code: "quota_exhausted" }, generation);
          return;
        }
        if (err instanceof ApiError && err.status === 409 && /disabled/i.test(err.message)) {
          dispatchSafe({ type: "FAILED", code: "camera_disabled", detail: err.message }, generation);
        } else if (err instanceof ApiError) {
          dispatchSafe({ type: "FAILED", code: "start_failed", detail: err.message }, generation);
        } else {
          dispatchSafe({ type: "FAILED", code: "start_failed" }, generation);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- attachPlayer is stable across renders (see below)
  }, [cameraId, tileId, slots, dispatchSafe, scheduleRenew, clearConnectTimeout, teardownPlayer, releaseLease, scheduleReconnect]);

  const attachPlayer = useCallback(
    (playlistUrl: string, generation: number, signal: AbortSignal) => {
      const video = videoRef.current;
      if (!video) return;

      if (Hls.isSupported()) {
        const hls = new Hls({
          // Buffer tuning: the biggest gap found when this design was modeled
          // on an existing player was that hls.js's defaults (unbounded
          // backBufferLength, ~60MB maxBufferSize per instance) are fine for
          // one video but not for a grid of up to nine. These caps bound each
          // tile to a modest, predictable footprint.
          backBufferLength: 8,
          maxBufferLength: 20,
          maxMaxBufferLength: 30,
          maxBufferSize: 20 * 1000 * 1000,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 6,
          enableWorker: true,
          // HLS delivery now sits behind requireAuth like every other route —
          // without this, the playlist and every segment fetch would omit
          // the session cookie and 401.
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
          },
        });
        hlsRef.current = hls;

        hls.on(Events.MANIFEST_PARSED, () => {
          if (generationRef.current !== generation) return;
          video.play().catch(() => {
            // Autoplay was refused. This is not a stream failure — see the
            // FRAG_LOADED handler below, which is what actually declares the
            // stream live, independent of whether this browser let it
            // autoplay. Left paused, the tile still shows real video; the
            // overlay's own click handler is the recovery path.
          });
        });

        // This, not the DOM "playing" event, is what proves the stream is
        // actually healthy. Relying on "playing" conflates two unrelated
        // things: whether the backend is delivering data, and whether this
        // browser's autoplay policy happened to let a muted <video> start on
        // its own. The two came apart in practice — a policy-blocked
        // autoplay left "playing" never firing, which meant the connect
        // timeout above fired on a stream that was working perfectly, tore
        // it down, and reported it as broken. A loaded fragment is real
        // evidence of a live stream regardless of what the video element's
        // paused state says.
        hls.on(Events.FRAG_LOADED, () => {
          if (generationRef.current !== generation) return;
          clearConnectTimeout();
          clearStallWatchdog();
          recoveryRef.current.noteProgress();
          dispatchSafe({ type: "PLAYING" }, generation);
          startProgressWatchdog(generation);
        });

        hls.on(Events.ERROR, (_evt, data) => {
          if (generationRef.current !== generation) return;
          if (!data.fatal) return;

          // Checked before every other branch: hls.js's own XHR loader (see
          // this player's xhrSetup above) carries the session cookie the same
          // as every other request, but it never passes through lib/api.ts's
          // request() — so its 401s never reach the global unauthorizedHandler
          // every other 401 in the app falls back to the login screen through.
          // Without this, a session ending mid-stream looked exactly like a
          // decode failure: several burned recovery attempts against a request
          // that can never succeed, ending in the same dead end a genuinely
          // broken stream reaches.
          if (data.response?.code === 401) {
            teardownPlayer();
            releaseLease();
            notifyUnauthorized();
            dispatchSafe({ type: "FAILED", code: "lease_lost", detail: undefined }, generation);
            return;
          }

          // Checked before the type-based branches below, because hls.js
          // reports this as a MEDIA_ERROR and it would otherwise be fed to the
          // recovery budget — several retries of a stream this device has no
          // decoder for, ending in "Repeated failures". The camera is fine and
          // the network is fine; this viewer just cannot decode H.265.
          if (
            data.details === ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR ||
            data.details === ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR
          ) {
            teardownPlayer();
            releaseLease();
            dispatchSafe({ type: "FAILED", code: "unsupported_codec", detail: undefined }, generation);
            return;
          }

          if (data.type === ErrorTypes.NETWORK_ERROR) {
            const decision = recoveryRef.current.request("network");
            if (!decision.allow) {
              // Not a dead end: the confirmed product decision is to keep
              // retrying slowly forever here, matching the backend's own
              // RTSP pull, which never gives up on a transient failure
              // either — see PlayerState's "reconnecting" comment.
              teardownPlayer();
              releaseLease();
              dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
              scheduleReconnect(generation);
              return;
            }
            setTimeout(() => {
              if (generationRef.current === generation) hls.startLoad();
            }, decision.delayMs);
            return;
          }

          if (data.type === ErrorTypes.MEDIA_ERROR) {
            const decision = recoveryRef.current.request("media");
            if (!decision.allow) {
              teardownPlayer();
              releaseLease();
              dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
              scheduleReconnect(generation);
              return;
            }
            setTimeout(() => {
              if (generationRef.current === generation) hls.recoverMediaError();
            }, decision.delayMs);
            return;
          }

          teardownPlayer();
          releaseLease();
          const code = data.details === ErrorDetails.MANIFEST_LOAD_ERROR ? "hls_network" : "hls_other";
          dispatchSafe({ type: "FAILED", code, detail: undefined }, generation);
        });

        hls.loadSource(playlistUrl);
        hls.attachMedia(video);
      } else {
        // Safari's native HLS path.
        video.src = playlistUrl;
        video.addEventListener(
          "loadedmetadata",
          () => {
            if (generationRef.current !== generation) return;
            video.play().catch(() => {});
          },
          { once: true, signal },
        );
      }

      // `signal` ties these to the generation's own AbortController, aborted
      // in teardownPlayer: without it, suspending and resuming a mounted tile
      // (focus mode toggling `enabled`, or Retry) adds a fresh pair of
      // listeners to the same <video> element every run and never removes
      // the previous pair.
      video.addEventListener(
        "playing",
        () => {
          if (generationRef.current !== generation) return;
          clearConnectTimeout();
          clearStallWatchdog();
          recoveryRef.current.noteProgress();
          dispatchSafe({ type: "PLAYING" }, generation);
          startProgressWatchdog(generation);
        },
        { signal },
      );
      video.addEventListener(
        "waiting",
        () => {
          dispatchSafe({ type: "STALLED" }, generation);

          // Not gated on the current state: this event can fire more than
          // once for the same underlying stall (a video element intermittently
          // re-reporting waiting), and each one deserves a fresh window rather
          // than being timed against however much of an earlier one already
          // elapsed. clearStallWatchdog() below always runs first, so there is
          // never more than one of these pending.
          clearStallWatchdog();
          stallWatchdogRef.current = setTimeout(() => {
            stallWatchdogRef.current = null;
            if (generationRef.current !== generation) return;
            // The same outcome a manual Retry produces, not a plain teardown:
            // an operator should not need to notice a frozen tile and click
            // anything themselves, per the same product decision F2 covers.
            teardownPlayer();
            releaseLease();
            dispatchSafe({ type: "RECOVERY_EXHAUSTED" }, generation);
            scheduleReconnect(generation);
          }, STALL_WATCHDOG_MS);
        },
        { signal },
      );
    },
    [
      dispatchSafe,
      releaseLease,
      teardownPlayer,
      clearConnectTimeout,
      clearStallWatchdog,
      scheduleReconnect,
      startProgressWatchdog,
    ],
  );

  const retry = useCallback(() => {
    teardownPlayer();
    start();
  }, [teardownPlayer, start]);

  // Stop watching: tear the player down and give the slot and lease back.
  //
  // This is the one control that actually costs the server nothing: the slot
  // is freed (so a queued tile starts immediately), the lease is released, and
  // once the last viewer's grace window closes the server stops pulling the
  // camera over the site link altogether. Pause below does none of that.
  const stopWatching = useCallback(() => {
    // Bump the generation before tearing down, so a start still in flight
    // cannot attach a player or record a lease for a tile the operator has
    // just stopped — the same guard every async path in start() checks.
    generationRef.current += 1;
    teardownPlayer();
    releaseLease();
    dispatch({ type: "STOPPED" });
  }, [teardownPlayer, releaseLease]);

  // Freeze the picture. Deliberately does NOT release the lease or stop the
  // pull: the operator is holding a frame to read something off it, and the
  // stream they are going to resume into must still be there. The lease keeps
  // renewing for the same reason, which is why holdsLease() counts a paused
  // tile. It saves no bandwidth, and the tooltip says so rather than letting
  // an operator believe otherwise.
  const pause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    dispatch({ type: "PAUSED" });
  }, []);

  // Resume at the LIVE EDGE, not where the picture froze.
  //
  // Live video has no meaningful "where you left off": while the frame was
  // held the world moved on, and simply calling play() resumes however many
  // seconds behind the present the buffer happens to hold — which on a
  // control-room wall means an operator watching the recent past while
  // believing it is now. The PLAYING dispatch comes from the video element's
  // own "playing" listener that attachPlayer registered.
  const resume = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const hls = hlsRef.current;
    if (hls && hls.liveSyncPosition !== null && Number.isFinite(hls.liveSyncPosition)) {
      video.currentTime = hls.liveSyncPosition;
    } else if (video.seekable.length > 0) {
      video.currentTime = video.seekable.end(video.seekable.length - 1);
    }
    video.play().catch(() => {
      // Autoplay policy can still refuse; the tile falls back to its
      // "Click to play" affordance, which is a real user gesture.
    });
  }, []);

  // `start` is deliberately recreated every render (it closes over cameraId,
  // tileId and slots), so effects that need to CALL it without re-running
  // whenever it changes identity read it from here instead of depending on it.
  const startRef = useRef(start);
  startRef.current = start;

  // A queued tile re-attempts as soon as the budget has room.
  //
  // Without this the store's own subscribe() was consumed only by the page's
  // badge, so nothing ever told a refused tile to try again: whichever tiles
  // happened to mount first won every slot, and the rest stayed dead until a
  // reload — with no retry button, because the state they landed in was
  // marked non-retryable.
  //
  // Three things this has to get right, all of which the naive version gets
  // wrong:
  //
  //  - A release notifies every waiting tile at once. If they all called
  //    start() immediately, one would win and the rest would thrash. Each
  //    waits a short random delay instead, so they arrive spread out.
  //  - A pending timer must never outlive the tile, or it starts a stream for
  //    a component that no longer exists — taking a slot nothing will release
  //    and a real RTSP pull server-side. React StrictMode makes that the
  //    normal path, not an edge case: it mounts, cleans up and mounts again.
  //    The cleanup below clears the timer, which covers every teardown this
  //    effect has, including unmount.
  //  - Subscribing inside start() — recreated every render — would add a
  //    listener per render and never remove them. The subscription belongs
  //    here, where there is a cleanup to match it.
  //
  // Deliberately NOT guarded on the generation counter the async paths in
  // start() use. Clearing the timer already covers teardown, and a generation
  // check would additionally reject a legitimate re-attempt: changing a
  // queued tile's camera bumps the generation without re-running this effect
  // (it does not depend on cameraId), so a pending timer would be ignored
  // forever and the tile would stick — the very bug being fixed here.
  useEffect(() => {
    if (!enabled || state.kind !== "queued") return;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = () => {
      if (timer !== undefined) return; // an attempt is already pending
      if (slots.active() >= slots.limit) return;
      timer = setTimeout(
        () => {
          timer = undefined;
          startRef.current();
        },
        QUEUE_RETRY_MIN_MS + Math.random() * QUEUE_RETRY_JITTER_MS,
      );
    };

    const unsubscribe = slots.subscribe(attempt);
    // A slot may have freed between the refusal and this subscription.
    attempt();

    return () => {
      unsubscribe();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [enabled, state.kind, slots]);

  // Teardown, keyed on the identity of what is being played.
  //
  // Deliberately split from the auto-start effect below, and deliberately does
  // NOT depend on autoStart. If it did, a camera becoming "already running"
  // would re-run this cleanup and tear down the very stream that made it
  // running — the tile would interrupt itself a second after the operator
  // pressed play, and leak the lease it had just been granted.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
      // teardownPlayer() aborts abortRef.current itself now, so this cleanup
      // doesn't need its own separate abort call.
      teardownPlayer();
      releaseLease();
      // Reset to idle so the auto-start effect can tell a torn-down tile from
      // a playing one. Without this the state stays "playing" after teardown,
      // and a tile suspended for focus mode would never resume.
      dispatch({ type: "STOPPED" });
    };
    // teardownPlayer/releaseLease are recreated per render by design (they
    // close over cameraId/tileId), so this effect intentionally keys only on
    // the identity+enabled triple that should actually end playback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, tileId, enabled]);

  // Auto-start, and ONLY when the camera is already being pulled for somebody
  // else.
  //
  // A tile no longer starts a stream simply by being on screen. Opening the
  // wall used to commit the site's uplink to every camera in view whether or
  // not anyone was watching them; now the first viewer of a camera asks for it
  // deliberately, and everyone after that joins something already running,
  // which costs nothing because the server keeps one RTSP session per camera
  // and shares it.
  //
  // The idle check is what makes this safe to re-run: autoStart flips to true
  // the moment this tile's own play succeeds, and without the guard the tile
  // would restart the stream it had just started.
  useEffect(() => {
    if (!enabled || !autoStart) return;
    if (stateRef.current.kind !== "idle") return;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, tileId, enabled, autoStart]);

  // A page-unload beacon: an unmount effect's cleanup can be skipped if the
  // tab is killed outright, so this is a second, independent path to the same
  // stop call.
  useEffect(() => {
    const onUnload = () => {
      const leaseId = leaseIdRef.current;
      if (leaseId) stopStreamOnUnload(cameraId, leaseId);
    };
    window.addEventListener("pagehide", onUnload);
    return () => window.removeEventListener("pagehide", onUnload);
  }, [cameraId]);

  // Background tabs get their timers throttled by the browser, which would
  // otherwise let a lease lapse on a dashboard left open on a second monitor.
  // Renewing immediately on foreground closes that gap.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!leaseIdRef.current) return;
      const generation = generationRef.current;
      api.stream.renew(cameraId, leaseIdRef.current).catch(() => {
        // The regular renew schedule will retry; this is just an early nudge.
      });
      scheduleRenew(generation);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [cameraId, scheduleRenew]);

  return {
    state,
    videoRef,
    retry,
    play: () => start(),
    /** Start by stopping the named running stream. The camera must be the one
     *  the server actually offered — it refuses a candidate that has since
     *  stopped rather than substituting whatever took its place. */
    playEvicting: (evictCameraID: string) => start(evictCameraID),
    pause,
    resume,
    stop: stopWatching,
    contention,
    dismissContention: () => setContention(null),
    holdsLease: holdsLease(state),
  };
}
